import { StageContainer } from '../../appeals/list/columns'
import dayjs from 'dayjs'
import { Value } from '../../elements'
import { Badge, Modal } from 'antd'
import React, { useState } from 'react'
import { IDeleteRecord, ISorterProps } from './interfaces'
import { ButtonActionWrapper } from '../../../components/list-operations/styles'
import { DeleteTwoTone } from '@ant-design/icons'

const formatPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return <Value data={undefined} />
  }
  const cleaned = ('' + phoneNumber).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/)
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`
  }
  return phoneNumber
}

export const getColumns = (
  sorter: ISorterProps,
  leader: boolean,
  deleteRecord: (id: number, removeClaim: boolean) => Promise<IDeleteRecord>,
) => {
  const columnsArr = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: 'Источник',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      sorter: true,
    },
    {
      title: 'Стадия',
      dataIndex: 'stage',
      key: 'stage',
      sorter: true,
      width: 150,
      render: stage => <StageContainer stage={stage} />,
    },
    {
      title: 'Дата рекламации',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 120,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Дата заказа',
      dataIndex: 'date_application',
      key: 'date_application',
      sorter: true,
      width: 120,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Ответственный дилер',
      dataIndex: 'dealer_code',
      key: 'dealer_code',
      width: 220,
      render: val => <Value data={val} />,
    },
    {
      title: 'Ответственный менеджер',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 200,
      render: val => <Value data={val} />,
    },
    {
      title: 'Номер тех. задания',
      dataIndex: 'num_tech',
      key: 'num_tech',
      width: 150,
    },
    {
      title: 'Новые сообщения',
      dataIndex: 'not_read',
      key: 'not_read',
      render: val => (+val > 0 ? <Badge count={+val} /> : '-'),
      align: 'center',
      sorter: true,
      width: 100,
    },
    {
      title: 'Адрес объекта',
      dataIndex: 'address',
      key: 'address',
      sorter: true,
      width: 300,
    },
    {
      title: 'ФИО',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200,
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      render: formatPhoneNumber,
      width: 150,
    },
    {
      title: 'Классификация',
      dataIndex: 'classification',
      key: 'classification',
      width: 150,
    },
    {
      title: 'Суть претензии',
      dataIndex: 'claim',
      key: 'claim',
      width: 400,
    },
    {
      title: 'Мероприятия дилера',
      dataIndex: 'dealer_event',
      key: 'dealer_event',
      width: 400,
    },
    {
      title: 'Требования дилера',
      dataIndex: 'dealer_requirement',
      key: 'dealer_requirement',
      width: 150,
    },
  ] as any[]

  const columnAction = {
    title: 'Действие',
    key: 'operation',
    dataIndex: 'operation',
    className: 'no-edit',
    fixed: 'right',
    width: 100,
    align: 'center',
    render: (_, { id }) => {
      return (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <DeleteButton id={id} deleteRecord={deleteRecord} />
        </span>
      )
    },
  }

  const DeleteButton = ({ id, deleteRecord }) => {
    const [deleteState, setDeleteState] = useState<'idle' | 'confirming' | 'confirmingWithClaim'>(
      'idle',
    )
    const [isDeleting, setIsDeleting] = useState(false)

    const modalConfig = {
      confirming: {
        title: 'Подтверждение удаления',
        content: `Вы уверены, что хотите удалить заявку № ${id}?`,
        okText: 'Да',
      },
      confirmingWithClaim: {
        title: 'Внимание!',
        content: `На основании этой заявки уже создана рекламация. Удалить заявку и рекламацию № ${id}?`,
        okText: 'Да, удалить заявку и рекламацию!',
      },
    }

    const { title, content, okText } = modalConfig[deleteState] || {}

    const handleInitialDelete = () => setDeleteState('confirming')

    const handleConfirmDelete = async () => {
      setIsDeleting(true)
      try {
        const result = await deleteRecord(id)
        if (result?.hasClaim) setDeleteState('confirmingWithClaim')
        else setDeleteState('idle')
      } finally {
        setIsDeleting(false)
      }
    }

    const handleForceDelete = async () => {
      setIsDeleting(true)
      try {
        await deleteRecord(id, true)
        setDeleteState('idle')
      } finally {
        setIsDeleting(false)
      }
    }

    const handleCancel = () => setDeleteState('idle')

    const okHandler = deleteState === 'confirming' ? handleConfirmDelete : handleForceDelete

    return (
      <>
        <ButtonActionWrapper title="Удалить" onClick={handleInitialDelete} disabled={isDeleting}>
          <DeleteTwoTone />
        </ButtonActionWrapper>

        <Modal
          title={title}
          open={deleteState !== 'idle'}
          onOk={okHandler}
          onCancel={handleCancel}
          okText={okText}
          cancelText="Нет"
          confirmLoading={isDeleting}
          closable={!isDeleting}
          maskClosable={!isDeleting}
          keyboard={!isDeleting}
          okButtonProps={{
            loading: isDeleting,
            type: 'primary',
            danger: true,
          }}
          cancelButtonProps={{ disabled: isDeleting }}
        >
          {content}
        </Modal>
      </>
    )
  }

  if (leader) {
    columnsArr.push(columnAction)
  }
  return columnsArr.map(item => {
    if (item.key === sorter.sort_field) {
      item.sortOrder = sorter.sort_order
    } else {
      delete item.sortOrder
    }
    return item
  })
}
